import { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import Navbar from "../components/Navbar";
import { toast } from "sonner";
import { CloudCog, Mail, ShieldCheck, ShieldOff, CreditCard } from "lucide-react";

export default function Settings() {
  const { user, refresh } = useAuth();
  const [threshold, setThreshold] = useState(user?.threshold_eur || 3000);
  const [saving, setSaving] = useState(false);
  const [airtable, setAirtable] = useState({ configured: false, base_name: "" });
  const [syncing, setSyncing] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);

  useEffect(() => {
    api.get("/airtable/status").then(r => setAirtable(r.data)).catch(()=>{});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/settings/threshold", { threshold_eur: Number(threshold) });
      await refresh();
      toast.success("Soglia aggiornata");
    } catch (ex) { toast.error(formatErr(ex.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/airtable/sync-movimenti");
      toast.success(`Inviati ${r.data.sent} movimenti ad Airtable`);
    } catch (ex) { toast.error(formatErr(ex.response?.data?.detail)); }
    finally { setSyncing(false); }
  };

  const sendTestReport = async () => {
    setSendingReport(true);
    try {
      await api.post("/admin/send-report-me");
      toast.success("Report inviato via email. Controlla la casella!");
    } catch (ex) {
      const s = ex.response?.status;
      if (s === 403) toast.error("Il report via email è disponibile con Piano Base o Plus");
      else toast.error(formatErr(ex.response?.data?.detail));
    } finally { setSendingReport(false); }
  };

  const revocaDelega = async () => {
    if (!window.confirm("Sei sicuro di voler revocare la delega? Le fatture non verranno più aggiornate automaticamente.")) return;
    try {
      await api.post("/delega/revoca");
      await refresh();
      toast.success("Delega revocata");
    } catch (ex) { toast.error(formatErr(ex.response?.data?.detail)); }
  };

  const [subStatus, setSubStatus] = useState(null);
  const [subLoading, setSubLoading] = useState(false);

  useEffect(() => {
    api.get("/subscriptions/status").then(r => setSubStatus(r.data)).catch(()=>{});
  }, [user?.stripe_subscription_id, user?.cancel_at_period_end]);

  const cancelSubscription = async () => {
    if (!window.confirm("Disattivare il rinnovo automatico? Continuerai ad avere accesso fino alla fine del periodo pagato.")) return;
    setSubLoading(true);
    try {
      await api.post("/subscriptions/cancel");
      await refresh();
      toast.success("Rinnovo automatico disattivato");
    } catch (ex) { toast.error(formatErr(ex.response?.data?.detail)); }
    finally { setSubLoading(false); }
  };

  const resumeSubscription = async () => {
    setSubLoading(true);
    try {
      await api.post("/subscriptions/resume");
      await refresh();
      toast.success("Rinnovo automatico riattivato");
    } catch (ex) { toast.error(formatErr(ex.response?.data?.detail)); }
    finally { setSubLoading(false); }
  };

  const openBillingPortal = async () => {
    setSubLoading(true);
    try {
      const r = await api.post("/subscriptions/portal");
      window.location.href = r.data.url;
    } catch (ex) { toast.error(formatErr(ex.response?.data?.detail)); setSubLoading(false); }
  };

  if (!user) return null;
  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="label-caps">Impostazioni</div>
        <h1 className="font-serif-display text-4xl text-[#1E3F33] mt-2">Il tuo account</h1>

        <div className="card-flat p-6 mt-8 space-y-1">
          <div className="text-sm text-[#6B6B6B]">Nome</div>
          <div className="font-medium">{user.name}</div>
          <div className="text-sm text-[#6B6B6B] mt-3">Email</div>
          <div className="font-medium">{user.email}</div>
          <div className="text-sm text-[#6B6B6B] mt-3">Settore</div>
          <div className="font-medium">{user.sector || "Non impostato"}</div>
          <div className="text-sm text-[#6B6B6B] mt-3">Piano attivo</div>
          <div className="font-medium">{user.plan ? user.plan : "Nessun piano"} {user.plan_expires_at ? `· scade il ${new Date(user.plan_expires_at).toLocaleDateString("it-IT")}` : ""}</div>
        </div>

        <div className="card-flat p-6 mt-6">
          <h3 className="font-serif-display text-2xl text-[#1E3F33]">Soglia di allerta</h3>
          <p className="text-sm text-[#6B6B6B] mt-1">Sarai avvisato quando il saldo IVA stimato supera questo importo.</p>
          <div className="flex gap-3 mt-4">
            <input data-testid="settings-threshold" type="number" min="0" step="100" value={threshold} onChange={e=>setThreshold(e.target.value)}
              className="px-3 py-2 border border-[#EBE5DF] rounded-md bg-white max-w-xs"/>
            <button data-testid="settings-threshold-save" onClick={save} disabled={saving}
              className="btn-lift px-4 py-2 bg-[#1E3F33] text-white rounded-md hover:bg-[#152D24] disabled:opacity-60">
              {saving ? "Salvo…" : "Salva"}
            </button>
          </div>
        </div>

        <div className="card-flat p-6 mt-6" data-testid="subscription-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-grow min-w-[260px]">
              <h3 className="font-serif-display text-2xl text-[#1E3F33] flex items-center gap-2"><CreditCard size={20}/> Abbonamento</h3>
              {!user.stripe_subscription_id ? (
                <>
                  <p className="mt-2 text-sm text-[#6B6B6B]">Nessun abbonamento attivo. {user.plan === "check_singolo" ? "Hai un accesso Check Singolo temporaneo." : ""}</p>
                  <a href="/piani" data-testid="sub-goto-pricing" className="btn-lift inline-block mt-4 px-4 py-2 bg-[#1E3F33] text-white rounded-md">Vedi i piani</a>
                </>
              ) : (
                <div className="mt-2 text-sm text-[#2C2C2C] space-y-1">
                  <div>Piano: <span className="font-medium capitalize">{user.plan}</span></div>
                  <div>Stato: <span className={`font-medium ${user.subscription_status === "active" ? "text-[#2D5A47]" : "text-[#C25934]"}`}>
                    {user.subscription_status === "active" ? "Attivo" :
                     user.subscription_status === "past_due" ? "Pagamento fallito · in periodo di grazia" :
                     user.subscription_status === "canceled" ? "Cancellato" : user.subscription_status}
                  </span></div>
                  {subStatus?.current_period_end && (
                    <div className="text-xs text-[#6B6B6B]">Prossimo rinnovo: {new Date(subStatus.current_period_end).toLocaleDateString("it-IT")}</div>
                  )}
                  {user.cancel_at_period_end && (
                    <div className="text-xs text-[#C25934] mt-2">
                      Il rinnovo automatico è disattivato. Manterrai l'accesso fino al {subStatus?.current_period_end && new Date(subStatus.current_period_end).toLocaleDateString("it-IT")}.
                    </div>
                  )}
                  {user.grace_period_until && user.subscription_status === "past_due" && (
                    <div className="text-xs text-[#A83C22] mt-2 p-2 border border-[#A83C22] rounded bg-[#FDF3EE]">
                      Pagamento non riuscito. Hai tempo fino al <strong>{new Date(user.grace_period_until).toLocaleDateString("it-IT")}</strong> per aggiornare il metodo di pagamento prima della sospensione.
                    </div>
                  )}
                </div>
              )}
            </div>
            {user.stripe_subscription_id && (
              <div className="flex flex-col gap-2 shrink-0">
                <button data-testid="sub-portal" onClick={openBillingPortal} disabled={subLoading}
                  className="btn-lift px-4 py-2 bg-[#1E3F33] text-white rounded-md hover:bg-[#152D24] disabled:opacity-60">
                  {subLoading ? "Apertura…" : "Gestisci pagamento"}
                </button>
                {user.cancel_at_period_end ? (
                  <button data-testid="sub-resume" onClick={resumeSubscription} disabled={subLoading}
                    className="btn-lift px-4 py-2 border border-[#2D5A47] text-[#2D5A47] rounded-md hover:bg-[#EBE5DF] disabled:opacity-60">
                    Riattiva rinnovo
                  </button>
                ) : (
                  <button data-testid="sub-cancel" onClick={cancelSubscription} disabled={subLoading}
                    className="btn-lift px-4 py-2 border border-[#A83C22] text-[#A83C22] rounded-md hover:bg-[#FDF3EE] disabled:opacity-60">
                    Annulla rinnovo
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="card-flat p-6 mt-6" data-testid="delega-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-grow min-w-[260px]">
              <h3 className="font-serif-display text-2xl text-[#1E3F33] flex items-center gap-2">
                {user.delega_stato === "attiva" ? <ShieldCheck size={20} className="text-[#2D5A47]"/> : <ShieldOff size={20} className="text-[#C25934]"/>}
                Delega Cassetto Fiscale (SPID)
              </h3>
              {user.delega_stato === "attiva" ? (
                <div className="mt-2 text-sm text-[#2C2C2C]">
                  <div>Stato: <span className="text-[#2D5A47] font-medium">Attiva</span></div>
                  {user.delega_attivata_at && <div className="text-xs text-[#6B6B6B] mt-1">Attivata il {new Date(user.delega_attivata_at).toLocaleDateString("it-IT")}</div>}
                  {user.codice_fiscale && <div className="text-xs text-[#6B6B6B] font-mono mt-1">CF: {user.codice_fiscale}</div>}
                  <div className="text-xs text-[#6B6B6B] mt-2">Le tue fatture vengono scaricate automaticamente dal Cassetto Fiscale.</div>
                </div>
              ) : (
                <p className="mt-2 text-sm text-[#6B6B6B]">Delega non attiva. Autorizza IVA Radar con SPID per ricevere le fatture automaticamente e non caricarle più a mano.</p>
              )}
            </div>
            {user.delega_stato === "attiva" ? (
              <button data-testid="delega-revoca" onClick={revocaDelega} className="btn-lift px-4 py-2 border border-[#A83C22] text-[#A83C22] rounded-md hover:bg-[#FDF3EE] shrink-0">
                Revoca delega
              </button>
            ) : (
              <a href="/autorizza-fatture" data-testid="delega-attiva-cta" className="btn-lift px-4 py-2 bg-[#0066CC] text-white rounded-md hover:bg-[#004E9E] shrink-0">
                Attiva delega
              </a>
            )}
          </div>
        </div>

        <div className="card-flat p-6 mt-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-serif-display text-2xl text-[#1E3F33] flex items-center gap-2"><Mail size={20}/> Report via email</h3>
              <p className="text-sm text-[#6B6B6B] mt-1">Con Piano Base o Plus riceverai un report quindicinale automatico via email con il saldo IVA stimato del periodo. Puoi provarlo ora:</p>
              <p className="text-xs text-[#6B6B6B] mt-2">Piano attuale: <span className="font-medium">{user.plan || "nessuno"}</span></p>
            </div>
            <button data-testid="send-test-report" onClick={sendTestReport} disabled={sendingReport || !["base","plus"].includes(user.plan)}
              className="btn-lift px-4 py-2 border border-[#1E3F33] rounded-md text-[#1E3F33] hover:bg-[#EBE5DF] disabled:opacity-50 shrink-0">
              {sendingReport ? "Invio…" : "Invia report di prova"}
            </button>
          </div>
        </div>

        <div className="card-flat p-6 mt-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-serif-display text-2xl text-[#1E3F33] flex items-center gap-2"><CloudCog size={20}/> Sincronizzazione Airtable</h3>
              <p className="text-sm text-[#6B6B6B] mt-1">Base "IVA Radar - Motore Calcolo". Le regole di calcolo e le liquidazioni periodiche sono gestite lato Airtable.</p>
              <p className="text-xs text-[#6B6B6B] mt-2">
                Stato: <span className={airtable.configured ? "text-[#2D5A47]" : "text-[#C25934]"}>{airtable.configured ? "connesso" : "PAT non ancora configurato"}</span>
                {airtable.base_id && <span> · Base ID: <span className="font-mono">{airtable.base_id}</span></span>}
              </p>
              {!airtable.configured && (
                <div className="mt-3 text-xs text-[#6B6B6B] bg-[#F9F8F6] border border-[#EBE5DF] rounded p-3">
                  Per attivare la sync: aggiungi <span className="font-mono">AIRTABLE_PAT</span> nelle variabili d'ambiente del backend. Genera un token su <a className="underline text-[#1E3F33]" href="https://airtable.com/create/tokens" target="_blank" rel="noreferrer">airtable.com/create/tokens</a> con scope <span className="font-mono">data.records:read + data.records:write</span> per la base <span className="font-mono">{airtable.base_id || "IVA Radar - Motore Calcolo"}</span>.
                </div>
              )}
            </div>
            <button data-testid="airtable-sync" onClick={sync} disabled={syncing || !airtable.configured}
              className="btn-lift px-4 py-2 border border-[#1E3F33] rounded-md text-[#1E3F33] hover:bg-[#EBE5DF] disabled:opacity-50 shrink-0">
              {syncing ? "Sync…" : "Invia movimenti"}
            </button>
          </div>
          <p className="mt-4 text-xs text-[#6B6B6B] italic">
            L'app scriverà i movimenti nella tabella <span className="font-mono">Movimenti IVA</span>, leggerà le <span className="font-mono">Regole IVA per Settore</span> per applicare le detraibilità corrette e mostrerà le <span className="font-mono">Liquidazioni IVA</span> già calcolate nel cruscotto.
          </p>
        </div>
      </div>
    </div>
  );
}
